import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, ReceiptText, Store, WifiOff } from 'lucide-react'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { Modal } from '@/components/Modal'
import { Badge, Button, ErrorNote, InfoNote } from '@/components/ui'
import { OfferDetailHeader } from './OfferDetailHeader'
import { OfferValue } from './OfferValue'
import { HoldToRedeem } from './HoldToRedeem'
import { UsedStamp } from './UsedStamp'
import { RedeemConfirmation, type RedeemResult } from './RedeemConfirmation'
import { describeOffer, shortOfferLabel } from '@shared/offers'
import {
  CATEGORY_LABELS,
  COLLECTIONS,
  type BookletStatus,
  type Offer,
  type Redemption,
} from '@shared/model'
import { formatDate, formatSek } from '@/lib/format'
import { redeemOffer } from '@/lib/api'
import { authErrorMessage } from '@/lib/authErrors'
import { useAuth } from '@/lib/auth'
import { useOnline } from '@/hooks/useOnline'

interface OfferDetailProps {
  offer: Offer | null
  onClose: () => void
  /** Hur många gånger erbjudandet fortfarande går att använda. */
  remaining: number
  /** Antal häften kontot äger för perioden – styr om "x av y" visas. */
  owned: number
  bookletStatus: BookletStatus
  bookletEndDate: string
  bookletStartDate: string
}

export function OfferDetail({
  offer,
  onClose,
  remaining,
  owned,
  bookletStatus,
  bookletEndDate,
  bookletStartDate,
}: OfferDetailProps) {
  const { user, profile } = useAuth()
  const online = useOnline()

  const [busy, setBusy] = useState(false)
  const [fetchingReceipt, setFetchingReceipt] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<RedeemResult | null>(null)
  /** True när kvittot visas i efterhand och inte just har skapats. */
  const [historic, setHistoric] = useState(false)

  const offerId = offer?.id
  const bookletId = offer?.bookletId

  /**
   * Letar upp en redan gjord inlösen av det här erbjudandet.
   *
   * Används till två saker som ser olika ut men är samma problem: att kunna
   * visa kvittot igen efter att man tryckt Klart, och att kunna skilja "det
   * gick fel" från "det gick igenom men svaret kom bort". Två likhetsfilter
   * behöver inget sammansatt index – Firestore slår ihop enfältsindexen.
   */
  const findMyRedemption = useCallback(async (): Promise<RedeemResult | null> => {
    if (!user || !offerId || !offer) return null
    try {
      const snapshot = await getDocs(
        query(
          collection(db, COLLECTIONS.redemptions),
          where('userUid', '==', user.uid),
          where('offerId', '==', offerId),
        ),
      )

      const found = snapshot.docs
        .map((document) => document.data() as Redemption)
        .filter((redemption) => !redemption.revoked)
        .sort((a, b) => (b.redeemedAt?.toMillis?.() ?? 0) - (a.redeemedAt?.toMillis?.() ?? 0))[0]

      if (!found) return null

      return {
        shortId: found.shortId,
        redeemedAt: found.redeemedAt?.toMillis?.() ?? Date.now(),
        valueSek: found.valueSek,
        totalSavedSek: bookletId
          ? (profile?.savingsByBooklet?.[bookletId]?.totalSek ?? found.valueSek)
          : found.valueSek,
        offerTitle: found.offerTitle,
        companyName: found.companyName,
        offerText: describeOffer(offer.offerTerms),
        staffInstructions: offer.staffInstructions ?? null,
      }
    } catch {
      return null
    }
  }, [bookletId, offer, offerId, profile, user])

  if (!offer) return null

  const offerText = describeOffer(offer.offerTerms)
  const usedUp = remaining <= 0

  async function handleRedeem() {
    if (!offer) return
    setError(null)
    setBusy(true)
    try {
      const response = await redeemOffer({ offerId: offer.id })
      setHistoric(false)
      setResult({
        shortId: response.shortId,
        redeemedAt: response.redeemedAt,
        valueSek: response.valueSek,
        totalSavedSek: response.totalSavedSek,
        offerTitle: offer.title,
        companyName: offer.companyName,
        offerText,
        staffInstructions: offer.staffInstructions ?? null,
      })
    } catch (caught) {
      /*
       * Servern skriver inlösen först och gör statistik och loggning efteråt.
       * Tappas svaret någonstans där emellan är rabatten förbrukad – men
       * användaren fick ett fel. Hen står i kassan och vet inte om det gick
       * igenom. Innan vi säger att något gått snett kollar vi därför om
       * inlösen faktiskt finns.
       */
      const existing = await findMyRedemption()
      if (existing) {
        setHistoric(true)
        setResult(existing)
        return
      }

      setError(
        online
          ? authErrorMessage(caught)
          : 'Ingen uppkoppling just nu. Rabatten behöver internet för att registreras – prova igen om en stund.',
      )
    } finally {
      setBusy(false)
    }
  }

  /** "Visa kvittot igen" – efter att man råkat trycka Klart för tidigt. */
  async function showReceipt() {
    setFetchingReceipt(true)
    setError(null)
    try {
      const existing = await findMyRedemption()
      if (existing) {
        setHistoric(true)
        setResult(existing)
      } else {
        setError('Vi hittar inget kvitto för den här rabatten just nu. Är du uppkopplad?')
      }
    } finally {
      setFetchingReceipt(false)
    }
  }

  function closeAll() {
    setResult(null)
    setHistoric(false)
    setError(null)
    onClose()
  }

  if (result) {
    return <RedeemConfirmation result={result} historic={historic} onClose={closeAll} />
  }

  return (
    <Modal open={Boolean(offer)} onClose={onClose} title={offer.title} hideTitle size="sm">
      <div className="space-y-5">
        {/*
         * Företagets egen design överst, samma som i bläddringsläget.
         *
         * Loggan och företagsnamnet ligger nu i bandet, så raden under är bara
         * det bandet inte kan vara: en länk vidare till företaget, och vilken
         * kategori det hör till.
         */}
        <OfferDetailHeader offer={offer} brandColor={offer.companyBrandColor} usedUp={usedUp} />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link
            to={`/foretag/${offer.companyId}`}
            onClick={onClose}
            className="tap-safe inline-flex items-center gap-1 text-sm font-semibold text-brand-ink underline underline-offset-2"
          >
            <Store size={14} aria-hidden />
            {offer.companyName}
          </Link>
          <Badge>{CATEGORY_LABELS[offer.companyCategory]}</Badge>
        </div>

        <h2 className="font-display text-xl font-extrabold text-balance">{offer.title}</h2>

        <div className="flex items-center gap-3 rounded-card bg-surface-2 p-3">
          <OfferValue terms={offer.offerTerms} size="lg" muted={usedUp} />
          <div className="min-w-0">
            <p className="font-semibold text-ink">{offerText}</p>
            <p className="text-sm text-muted">Värde ca {formatSek(offer.estimatedValueSek)}</p>
          </div>
        </div>

        {offer.description && <p className="text-ink">{offer.description}</p>}

        {offer.terms && (
          <p className="rounded-xl border border-border bg-surface-2 px-3.5 py-3 text-sm text-muted">
            {offer.terms}
          </p>
        )}

        {/* Kassörens instruktion, t.ex. ett PLU-nummer. Köparen behöver se den
            i förväg för att kunna säga till – och den upprepas på kvittot. */}
        {offer.staffInstructions && (
          <div className="rounded-xl border-2 border-accent-strong bg-accent/15 px-3.5 py-3">
            <p className="text-xs font-bold tracking-wide text-muted uppercase">Till kassören</p>
            <p className="mt-0.5 font-semibold text-ink">{offer.staffInstructions}</p>
          </div>
        )}

        {owned > 1 && !usedUp && (
          <InfoNote>
            Du äger {owned} häften den här perioden, så erbjudandet kan användas {remaining} gånger
            till.
          </InfoNote>
        )}

        {error && <ErrorNote>{error}</ErrorNote>}

        {/* Vad som går att göra beror på periodens status och hur många
            uttag som är kvar. */}
        {bookletStatus === 'upcoming' ? (
          <InfoNote>
            <CalendarClock size={16} className="mr-1.5 -mt-0.5 inline" aria-hidden />
            Häftet aktiveras {formatDate(bookletStartDate)}. Då går erbjudandet att använda.
          </InfoNote>
        ) : bookletStatus === 'expired' ? (
          <InfoNote>
            Häftet gick ut {formatDate(bookletEndDate)}. Du kan fortfarande läsa erbjudandena, men
            de går inte längre att använda.
          </InfoNote>
        ) : usedUp ? (
          // Neutralt, inte grönt. Grönt betyder "det gick bra" – men här finns
          // inget kvar att använda, och det är ett annat besked.
          <div className="space-y-3 rounded-card border-2 border-border-strong bg-surface-2 p-4">
            <div className="flex items-center gap-4">
              <UsedStamp size="lg" className="shrink-0" />
              <p className="text-sm text-muted">
                {owned > 1
                  ? 'Du har använt erbjudandet på båda dina häften.'
                  : 'Rabatten är förbrukad och går inte att få tillbaka.'}
              </p>
            </div>
            {/* Kvittot var tidigare borta för alltid så fort man tryckt Klart –
                vilket man gör när man är stressad i en kassa. */}
            <Button
              variant="quiet"
              fullWidth
              loading={fetchingReceipt}
              icon={<ReceiptText size={17} />}
              onClick={() => void showReceipt()}
            >
              Visa kvittot igen
            </Button>
          </div>
        ) : (
          <HoldToRedeem
            onComplete={() => void handleRedeem()}
            busy={busy}
            // Vad som löses in, så att bekräftelserutan kan säga det. Den som
            // kommer dit ser inte nödvändigtvis erbjudandet bakom rutan.
            description={`${shortOfferLabel(offer.offerTerms)} hos ${offer.companyName}`}
          />
        )}

        {!online && !usedUp && bookletStatus === 'active' && (
          <p className="flex items-center justify-center gap-1.5 text-xs text-muted">
            <WifiOff size={14} aria-hidden />
            Offline – du kan visa erbjudandet, men inlösen kräver uppkoppling.
          </p>
        )}
      </div>
    </Modal>
  )
}
